import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getCustomerToken() {
  return localStorage.getItem("istring_customer_token");
}

function setCustomerToken(token) {
  localStorage.setItem("istring_customer_token", token);
}

function clearCustomerToken() {
  localStorage.removeItem("istring_customer_token");
}

function PillButton({ children, onClick, variant = "primary", disabled, className, testId, type = "button" }) {
  const styles =
    variant === "primary"
      ? "bg-zinc-900 text-white hover:bg-zinc-800"
      : variant === "secondary"
        ? "bg-white/70 text-zinc-900 hover:bg-white border border-zinc-200"
        : "bg-transparent text-zinc-900 hover:bg-white/60 border border-zinc-200";

  return (
    <button
      data-testid={testId}
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-medium",
        "transition-colors duration-200",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        styles,
        className,
      )}
    >
      {children}
    </button>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testId, required, min, max, disabled }) {
  return (
    <label className="block" data-testid={`${testId}-wrapper`}>
      <div className="mb-1 text-sm text-zinc-700" data-testid={`${testId}-label`}>
        {label} {required ? <span className="text-zinc-400">*</span> : null}
      </div>
      <input
        data-testid={testId}
        value={value}
        onChange={onChange}
        type={type}
        placeholder={placeholder}
        min={min}
        max={max}
        disabled={disabled}
        className={cx(
          "w-full rounded-2xl border border-zinc-200 bg-white/70 px-4 py-3 text-base text-zinc-900",
          "outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200",
          disabled ? "opacity-60" : "opacity-100",
        )}
      />
    </label>
  );
}

function Select({ label, value, onChange, testId, children }) {
  return (
    <label className="block" data-testid={`${testId}-wrapper`}>
      <div className="mb-1 text-sm text-zinc-700" data-testid={`${testId}-label`}>
        {label}
      </div>
      <select
        data-testid={testId}
        value={value}
        onChange={onChange}
        className={cx(
          "w-full rounded-2xl border border-zinc-200 bg-white/70 px-4 py-3 text-base text-zinc-900",
          "outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200",
        )}
      >
        {children}
      </select>
    </label>
  );
}

function Checkbox({ checked, onChange, label, testId }) {
  return (
    <label
      className="flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white/70 px-4 py-3"
      data-testid={`${testId}-wrapper`}
    >
      <input data-testid={testId} type="checkbox" checked={Boolean(checked)} onChange={onChange} className="mt-1 h-4 w-4" />
      <div className="text-sm text-zinc-800" data-testid={`${testId}-label`}>
        {label}
      </div>
    </label>
  );
}

function Card({ children, className, testId }) {
  return (
    <div
      data-testid={testId}
      className={cx(
        "rounded-3xl border border-zinc-200 bg-white/70 shadow-sm",
        "backdrop-blur supports-[backdrop-filter]:bg-white/60",
        className,
      )}
    >
      {children}
    </div>
  );
}

function Toast({ toast, onClose }) {
  if (!toast) return null;
  const tone =
    toast.type === "error" ? "border-rose-200 bg-rose-50 text-rose-900" : "border-emerald-200 bg-emerald-50 text-emerald-900";
  return (
    <div className="fixed right-5 top-5 z-50" data-testid="toast-container">
      <div className={cx("max-w-sm rounded-2xl border px-4 py-3 shadow-lg", tone)} data-testid="toast-message">
        <div className="flex items-start justify-between gap-3">
          <div className="text-sm font-medium" data-testid="toast-message-text">
            {toast.message}
          </div>
          <button
            data-testid="toast-close-button"
            onClick={onClose}
            className="rounded-full px-2 py-1 text-xs text-current/70 hover:text-current"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomerNav() {
  const token = getCustomerToken();
  if (!token) {
    return (
      <Link to="/account/login" data-testid="nav-account-link" className="hidden sm:block">
        <PillButton testId="nav-account-button" variant="ghost">
          Account
        </PillButton>
      </Link>
    );
  }
  return (
    <Link to="/account" data-testid="nav-account-dashboard-link" className="hidden sm:block">
      <PillButton testId="nav-account-dashboard-button" variant="ghost">
        Account
      </PillButton>
    </Link>
  );
}

function Shell({ children }) {
  return (
    <div
      className={cx(
        "min-h-screen",
        "flex flex-col",
        "bg-[radial-gradient(1100px_circle_at_10%_0%,rgba(0,0,0,0.06),transparent_40%),radial-gradient(900px_circle_at_90%_10%,rgba(0,0,0,0.04),transparent_35%),linear-gradient(to_bottom,rgba(250,250,250,1),rgba(244,244,245,1))]",
      )}
      data-testid="app-shell"
    >
      <header className="sticky top-0 z-40 border-b border-zinc-200/70 bg-white/60 backdrop-blur" data-testid="top-nav">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-brand-link">
            <img data-testid="nav-brand-logo" src="/istring-logo.png" alt="iString" className="h-8 w-auto" />
            <span className="sr-only" data-testid="nav-brand-text">
              iString
            </span>
          </Link>
          <nav className="flex items-center gap-2" data-testid="nav-links">
            <Link to="/book" data-testid="nav-book-link" className="hidden sm:block">
              <PillButton testId="nav-book-button" variant="secondary">
                Book
              </PillButton>
            </Link>

            <CustomerNav />

            {/* Admin link hidden for regular users; admin login is still available at /admin/login */}

            <Link to="/book" data-testid="nav-book-link-mobile" className="sm:hidden">
              <PillButton testId="nav-book-button-mobile" variant="primary">
                Book
              </PillButton>
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-10" data-testid="app-main">
        {children}
      </main>

      <footer className="mx-auto w-full max-w-6xl px-5 pb-8 text-sm text-zinc-500" data-testid="app-footer">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid="footer-row">
          <div data-testid="footer-text">© {new Date().getFullYear()} iString All rights reserved</div>
          <div className="flex flex-wrap items-center gap-4" data-testid="footer-links">
            <Link to="/privacy" className="hover:text-zinc-900 transition-colors duration-200" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-zinc-900 transition-colors duration-200" data-testid="footer-terms-link">
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PrivacyPolicyPage() {
  return (
    <Shell>
      <div className="max-w-3xl" data-testid="privacy-page">
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="privacy-title">
          Privacy Policy
        </h1>
        <p className="mt-4 text-sm text-zinc-600" data-testid="privacy-updated">
          Last updated: {new Date().toLocaleDateString()}
        </p>

        <div className="prose prose-zinc mt-6 max-w-none" data-testid="privacy-body">
          <p>
            This Privacy Policy explains how iString collects, uses, and shares information when you book an appointment or use our website.
          </p>
          <h2>Information we collect</h2>
          <ul>
            <li>Contact details (name, email, phone number)</li>
            <li>Pickup &amp; delivery address</li>
            <li>Appointment details and preferences</li>
            <li>Payment confirmation information from PayPal (we do not store full payment card details)</li>
          </ul>
          <h2>How we use information</h2>
          <ul>
            <li>To schedule and fulfill your stringing appointment</li>
            <li>To contact you about your booking (including SMS if you opt in)</li>
            <li>To improve our services and customer experience</li>
          </ul>
          <h2>SMS consent</h2>
          <p>
            If you opt in to receive SMS updates, you consent to receive messages about your appointment. Message frequency varies. Message &amp; data rates may apply.
            You can reply STOP to unsubscribe.
          </p>
          <h2>Sharing</h2>
          <p>
            We may share information with service providers used to operate the service (e.g., payment processing and messaging providers) and as required by law.
          </p>
          <h2>Contact</h2>
          <p>
            For privacy questions, contact us at <a href="mailto:info@istring.club">info@istring.club</a>.
          </p>
        </div>
      </div>
    </Shell>
  );
}

function TermsPage() {
  return (
    <Shell>
      <div className="max-w-3xl" data-testid="terms-page">
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="terms-title">
          Terms &amp; Conditions
        </h1>
        <p className="mt-4 text-sm text-zinc-600" data-testid="terms-updated">
          Last updated: {new Date().toLocaleDateString()}
        </p>

        <div className="prose prose-zinc mt-6 max-w-none" data-testid="terms-body">
          <p>
            By booking an appointment with iString, you agree to these Terms &amp; Conditions.
          </p>
          <h2>Appointments</h2>
          <ul>
            <li>Appointments are one hour and must be booked online.</li>
            <li>Bookings are confirmed only after payment is completed.</li>
            <li>You may reschedule or cancel confirmed bookings subject to availability.</li>
          </ul>
          <h2>Pickup &amp; delivery</h2>
          <p>Free pickup and delivery is available within 20 miles of 95816.</p>
          <h2>Pricing</h2>
          <p>Stringing service is a flat $25 per job (strings not included unless separately arranged).</p>
          <h2>SMS messages</h2>
          <p>
            If you opt in to SMS updates, message frequency varies. Message &amp; data rates may apply. Reply STOP to unsubscribe.
          </p>
          <h2>Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, iString will not be liable for indirect or consequential damages.
          </p>
          <h2>Contact</h2>
          <p>
            Questions? Email <a href="mailto:info@istring.club">info@istring.club</a>.
          </p>
        </div>
      </div>
    </Shell>
  );
}

function HomePage() {
  return (
    <Shell>
      <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]" data-testid="home-page">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/60 px-3 py-1 text-xs text-zinc-700" data-testid="home-pill">
            <span data-testid="home-pill-left">Tennis Racquet Stringing</span>
            <span className="text-zinc-400" data-testid="home-pill-dot">
              •
            </span>
            <span data-testid="home-pill-right">Serving the Sacramento Area</span>
          </div>
          <h1 className="mt-5 text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
            Premium stringing, on your schedule.
          </h1>
          <p className="mt-4 max-w-2xl text-base text-zinc-600 md:text-lg" data-testid="home-hero-subtitle">
            Book a one-hour appointment. Flat <span className="font-medium text-zinc-900">$25</span> string job.
            <br />
            Includes free pickup &amp; delivery within 20 miles of 95816.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="home-cta-row">
            <Link to="/book" data-testid="home-cta-book-link">
              <PillButton testId="home-cta-book-button" variant="primary" className="px-6 py-3 text-base">
                Book an appointment
              </PillButton>

            </Link>
            <a
              data-testid="home-cta-call-link"
              href="#how-it-works"
              className="rounded-full px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-white/70 transition-colors duration-200"
            >
              How it works
            </a>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-3" data-testid="home-feature-grid">
            {[
              { title: "Flat $25", desc: "No surprise fees." },
              { title: "Pickup + Delivery", desc: "Free within 20 miles of 95816." },
              { title: "String Advice", desc: "Tell us what you like." },
            ].map((f) => (
              <Card key={f.title} testId={`home-feature-${f.title.replace(/\s/g, "-").toLowerCase()}`} className="p-5">
                <div className="text-sm font-semibold text-zinc-900" data-testid={`home-feature-title-${f.title.replace(/\s/g, "-").toLowerCase()}`}>
                  {f.title}
                </div>
                <div className="mt-1 text-sm text-zinc-600" data-testid={`home-feature-desc-${f.title.replace(/\s/g, "-").toLowerCase()}`}>
                  {f.desc}
                </div>
              </Card>
            ))}
          </div>
        </div>

        <Card testId="home-side-card" className="p-6">
          <div className="text-sm font-semibold text-zinc-900" data-testid="home-side-title">
            Hours
          </div>
          <div className="mt-3 space-y-2 text-sm text-zinc-700" data-testid="home-hours-list">
            <div data-testid="home-hours-weekdays">Mon–Fri: 9:00 AM – 9:00 PM</div>
            <div data-testid="home-hours-weekends">Sat–Sun: 10:00 AM – 5:00 PM</div>
          </div>
          <div className="mt-6 border-t border-zinc-200 pt-5" id="how-it-works" data-testid="home-how-it-works">
            <div className="text-sm font-semibold text-zinc-900" data-testid="home-how-title">
              How it works
            </div>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-zinc-700" data-testid="home-how-steps">
              <li data-testid="home-how-step-1">Pick a time slot.</li>
              <li data-testid="home-how-step-2">Tell us your strings + tension.</li>
              <li data-testid="home-how-step-3">Pay $25 to confirm.</li>
              <li data-testid="home-how-step-4">We pick up, string, and deliver.</li>
            </ol>
          </div>
        </Card>
      </div>
    </Shell>
  );
}

function formatDateLabel(ymd) {
  try {
    const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return ymd;
  }
}

function addDaysYMD(baseYMD, days) {
  const [y, m, d] = baseYMD.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function todayYMD() {
  const now = new Date();
  const yy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function usePayPalScript(clientId) {
  const [ready, setReady] = useState(() => Boolean(window.paypal));

  useEffect(() => {
    if (!clientId) return;
    if (window.paypal) return;

    const existing = document.querySelector("script[data-paypal-sdk='true']");
    if (existing) {
      const onLoad = () => setReady(true);
      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }

    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture`;
    script.async = true;
    script.setAttribute("data-paypal-sdk", "true");

    const onLoad = () => setReady(true);
    const onError = () => setReady(false);

    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);

    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    };
  }, [clientId]);

  return ready;
}

function BookingPage() {
  const [toast, setToast] = useState(null);

  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const [date, setDate] = useState(todayYMD());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [within20, setWithin20] = useState(true);

  // Require explicit yes/no
  const [hasOwnStrings, setHasOwnStrings] = useState(null);
  const [needsAdvice, setNeedsAdvice] = useState(null);
  const [tensionMains, setTensionMains] = useState("");
  const [tensionCrosses, setTensionCrosses] = useState("");

  const [smsOptIn, setSmsOptIn] = useState(false);

  const [creatingOrder, setCreatingOrder] = useState(false);
  const createdOrderRef = useRef(null);

  const paypalReady = usePayPalScript(config?.paypal_client_id);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoadingConfig(true);
        const res = await axios.get(`${API}/config`);
        if (!mounted) return;
        setConfig(res.data);
      } catch {
        setToast({ type: "error", message: "Could not load app configuration." });
      } finally {
        if (mounted) setLoadingConfig(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const bookingWindowDates = useMemo(() => {
    const base = todayYMD();
    const days = config?.booking_window_days ?? 14;
    return Array.from({ length: days + 1 }, (_, i) => addDaysYMD(base, i));
  }, [config]);

  useEffect(() => {
    if (!date) return;
    let mounted = true;
    const loadSlots = async () => {
      try {
        setLoadingSlots(true);
        setSelectedSlot(null);
        const res = await axios.get(`${API}/slots`, { params: { date } });
        if (!mounted) return;
        setSlots(res.data);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Could not load slots.";
        setToast({ type: "error", message: msg });
      } finally {
        if (mounted) setLoadingSlots(false);
      }
    };
    loadSlots();
    return () => {
      mounted = false;
    };
  }, [date]);

  useEffect(() => {
    createdOrderRef.current = null;
    const container = document.getElementById("paypal-button-container");
    if (container) {
      container.innerHTML = "";
      container.setAttribute("data-rendered", "false");
    }
  }, [
    date,
    selectedSlot,
    firstName,
    lastName,
    email,
    phone,
    address,
    within20,
    hasOwnStrings,
    needsAdvice,
    tensionMains,
    tensionCrosses,
    smsOptIn,
  ]);

  const isFormValid = useMemo(() => {
    if (!selectedSlot) return false;
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!email.trim() || !phone.trim()) return false;
    if (!address.trim()) return false;
    if (!within20) return false;

    if (hasOwnStrings === null || needsAdvice === null) return false;

    // SMS opt-in is optional; we only require explicit consent when the user opts in.

    const tm = Number(tensionMains);
    const tc = Number(tensionCrosses);
    if (!Number.isFinite(tm) || !Number.isFinite(tc)) return false;
    if (tm < 10 || tm > 80) return false;
    if (tc < 10 || tc > 80) return false;

    return true;
  }, [selectedSlot, firstName, lastName, email, phone, address, within20, hasOwnStrings, needsAdvice, tensionMains, tensionCrosses]);

  const createOrderOnBackend = React.useCallback(async () => {
    if (createdOrderRef.current) return createdOrderRef.current;
    if (!isFormValid) {
      setToast({ type: "error", message: "Please complete all fields (including the questions) and select a time slot." });
      throw new Error("Form invalid");
    }

    setCreatingOrder(true);
    try {
      const payload = {
        slot_start_utc: selectedSlot.slot_start_utc,
        customer: { first_name: firstName, last_name: lastName, email, phone },
        pickup: { address, within_15_miles: within20 },
        questionnaire: {
          has_own_strings: Boolean(hasOwnStrings),
          needs_advice: Boolean(needsAdvice),
          tension_mains: Number(tensionMains),
          tension_crosses: Number(tensionCrosses),
        },
        sms_opt_in: Boolean(smsOptIn),
      };
      const res = await axios.post(`${API}/paypal/create-order`, payload);
      createdOrderRef.current = res.data.order_id;
      return res.data.order_id;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create PayPal order.";
      setToast({ type: "error", message: msg });
      throw e;
    } finally {
      setCreatingOrder(false);
    }
  }, [
    isFormValid,
    selectedSlot,
    firstName,
    lastName,
    email,
    phone,
    address,
    within20,
    hasOwnStrings,
    needsAdvice,
    tensionMains,
    tensionCrosses,
    smsOptIn,
  ]);

  useEffect(() => {
    const container = document.getElementById("paypal-button-container");

    if (!isFormValid) {
      if (container) {
        container.innerHTML = "";
        container.setAttribute("data-rendered", "false");
      }
      return;
    }

    if (!paypalReady) return;
    if (!window.paypal) return;
    if (!container) return;
    if (container.getAttribute("data-rendered") === "true") return;

    container.setAttribute("data-rendered", "true");

    try {
      window.paypal
        .Buttons({
          style: { layout: "vertical", color: "black", shape: "pill", label: "pay" },
          createOrder: async () => {
            const orderId = await createOrderOnBackend();
            return orderId;
          },
          onApprove: async (data) => {
            try {
              const res = await axios.post(`${API}/paypal/capture-order`, { order_id: data.orderID });
              window.location.href = `/confirmation/${encodeURIComponent(res.data.booking.booking_id)}?t=${encodeURIComponent(res.data.manage_token)}`;
            } catch (e) {
              const msg = e?.response?.data?.detail || "Payment capture failed.";
              setToast({ type: "error", message: msg });
              const c = document.getElementById("paypal-button-container");
              if (c) {
                c.innerHTML = "";
                c.setAttribute("data-rendered", "false");
              }
            }
          },
          onError: () => {
            setToast({ type: "error", message: "PayPal encountered an error. Please try again." });
            const c = document.getElementById("paypal-button-container");
            if (c) {
              c.innerHTML = "";
              c.setAttribute("data-rendered", "false");
            }
          },
        })
        .render("#paypal-button-container");
    } catch {
      // ignore
    }
  }, [paypalReady, createOrderOnBackend, isFormValid]);

  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]" data-testid="booking-page">
        <div className="space-y-4" data-testid="booking-left">
          <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="booking-title">
            Book a slot
          </h1>
          <p className="text-base text-zinc-600 md:text-lg" data-testid="booking-subtitle">
            One-hour appointments • <span className="font-medium text-zinc-900" data-testid="booking-price">${config?.price_usd ?? 25}</span> flat-fee •
            Sacramento Area
          </p>

          <Card testId="booking-slot-card" className="p-5">
            <div className="flex items-center justify-between gap-3" data-testid="booking-slot-card-header">
              <div className="text-sm font-semibold text-zinc-900" data-testid="booking-slot-card-title">
                Choose a date
              </div>
              <div className="text-xs text-zinc-500" data-testid="booking-slot-card-window">
                Next {config?.booking_window_days ?? 14} days
              </div>
            </div>

            <div className="mt-3">
              <Select label="Date" value={date} onChange={(e) => setDate(e.target.value)} testId="booking-date-select">
                {bookingWindowDates.map((d) => (
                  <option data-testid={`booking-date-option-${d}`} key={d} value={d}>
                    {formatDateLabel(d)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="mt-4" data-testid="booking-slots-section">
              <div className="mb-2 text-sm font-semibold text-zinc-900" data-testid="booking-slots-title">
                Available times
              </div>

              {loadingSlots ? (
                <div className="text-sm text-zinc-500" data-testid="booking-slots-loading">
                  Loading slots…
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2" data-testid="booking-slots-grid">
                  {slots.map((s, idx) => {
                    const active = selectedSlot?.slot_start_utc === s.slot_start_utc;
                    return (
                      <button
                        key={s.slot_start_utc}
                        data-testid={`booking-slot-button-${idx}`}
                        disabled={!s.available}
                        onClick={() => setSelectedSlot(s)}
                        className={cx(
                          "rounded-2xl border px-3 py-3 text-left",
                          "transition-colors duration-200",
                          s.available ? "bg-white/70 hover:bg-white" : "bg-zinc-100 text-zinc-400 cursor-not-allowed",
                          active ? "border-zinc-900 ring-2 ring-zinc-200" : "border-zinc-200",
                        )}
                      >
                        <div className="text-sm font-semibold" data-testid={`booking-slot-label-${idx}`}>
                          {s.slot_start_local_label}
                        </div>
                        <div className="mt-0.5 text-xs text-zinc-500" data-testid={`booking-slot-availability-${idx}`}>
                          {s.available ? "Available" : "Unavailable"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-4" data-testid="booking-right">
          <Card testId="booking-form-card" className="p-6">
            <div className="flex items-center justify-between" data-testid="booking-form-header">
              <div className="text-sm font-semibold text-zinc-900" data-testid="booking-form-title">
                Details
              </div>
              <div className="text-xs text-zinc-500" data-testid="booking-selected-slot">
                {selectedSlot ? `Selected: ${selectedSlot.slot_start_local_label}` : "Select a time"}
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="booking-contact-grid">
              <Input label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} testId="booking-first-name" required />
              <Input label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} testId="booking-last-name" required />
              <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} testId="booking-email" type="email" required />
              <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} testId="booking-phone" required />
            </div>

            <div className="mt-4" data-testid="booking-address-section">
              <Input
                label="Pickup & delivery address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                testId="booking-address"
                required
                placeholder="Street, city, ZIP"
              />
            </div>

            <div className="mt-3" data-testid="booking-within20-section">
              <Checkbox
                testId="booking-within-15-checkbox"
                checked={within20}
                onChange={(e) => setWithin20(e.target.checked)}
                label="I confirm this address is within 20 miles of 95816 (free pickup & delivery)."
              />
            </div>

            <div className="mt-3" data-testid="booking-sms-optin-section">
              <Checkbox
                testId="booking-sms-optin-checkbox"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                label="I agree to receive SMS updates about my appointment. Message & data rates may apply. Reply STOP to unsubscribe."
              />
            </div>

            <div className="mt-6 border-t border-zinc-200 pt-5" data-testid="booking-questions-section">
              <div className="text-sm font-semibold text-zinc-900" data-testid="booking-questions-title">
                Stringing questions
              </div>

              <div className="mt-3 grid gap-3" data-testid="booking-questions-grid">
                <div className="grid gap-3" data-testid="booking-yn-questions">
                  <div className="rounded-2xl border border-zinc-200 bg-white/70 px-4 py-4" data-testid="booking-own-strings-card">
                    <div className="text-sm font-medium text-zinc-900" data-testid="booking-own-strings-question">
                      Do you have your own strings?
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="booking-own-strings-options">
                      <PillButton
                        testId="booking-own-strings-yes"
                        variant={hasOwnStrings === true ? "primary" : "secondary"}
                        onClick={() => setHasOwnStrings(true)}
                        className="px-5"
                      >
                        Yes
                      </PillButton>
                      <PillButton
                        testId="booking-own-strings-no"
                        variant={hasOwnStrings === false ? "primary" : "secondary"}
                        onClick={() => setHasOwnStrings(false)}
                        className="px-5"
                      >
                        No
                      </PillButton>
                      {hasOwnStrings === null ? (
                        <div className="text-xs text-zinc-500" data-testid="booking-own-strings-required">
                          Please choose Yes or No.
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-zinc-200 bg-white/70 px-4 py-4" data-testid="booking-needs-advice-card">
                    <div className="text-sm font-medium text-zinc-900" data-testid="booking-needs-advice-question">
                      Do you need advice about strings?
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="booking-needs-advice-options">
                      <PillButton
                        testId="booking-needs-advice-yes"
                        variant={needsAdvice === true ? "primary" : "secondary"}
                        onClick={() => setNeedsAdvice(true)}
                        className="px-5"
                      >
                        Yes
                      </PillButton>
                      <PillButton
                        testId="booking-needs-advice-no"
                        variant={needsAdvice === false ? "primary" : "secondary"}
                        onClick={() => setNeedsAdvice(false)}
                        className="px-5"
                      >
                        No
                      </PillButton>
                      {needsAdvice === null ? (
                        <div className="text-xs text-zinc-500" data-testid="booking-needs-advice-required">
                          Please choose Yes or No.
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2" data-testid="booking-tension-grid">
                  <Input
                    label="Tension on mains (lbs)"
                    value={tensionMains}
                    onChange={(e) => setTensionMains(e.target.value)}
                    testId="booking-tension-mains"
                    type="number"
                    min={10}
                    max={80}
                    required
                    placeholder="e.g., 52"
                  />
                  <Input
                    label="Tension on crosses (lbs)"
                    value={tensionCrosses}
                    onChange={(e) => setTensionCrosses(e.target.value)}
                    testId="booking-tension-crosses"
                    type="number"
                    min={10}
                    max={80}
                    required
                    placeholder="e.g., 50"
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card testId="booking-summary-card" className="p-6">
            <div className="text-sm font-semibold text-zinc-900" data-testid="booking-summary-title">
              Summary
            </div>
            <div className="mt-3 space-y-2 text-sm text-zinc-700" data-testid="booking-summary-body">
              <div className="flex items-center justify-between" data-testid="booking-summary-row-slot">
                <span data-testid="booking-summary-label-slot">Time</span>
                <span className="font-medium text-zinc-900" data-testid="booking-summary-value-slot">
                  {selectedSlot ? selectedSlot.slot_start_local_label : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between" data-testid="booking-summary-row-price">
                <span data-testid="booking-summary-label-price">Price</span>
                <span className="font-medium text-zinc-900" data-testid="booking-summary-value-price">
                  ${config?.price_usd ?? 25}
                </span>
              </div>
              <div className="flex items-center justify-between" data-testid="booking-summary-row-pickup">
                <span data-testid="booking-summary-label-pickup">Pickup/Delivery</span>
                <span className="font-medium text-zinc-900" data-testid="booking-summary-value-pickup">
                  Included
                </span>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-zinc-200 bg-white/60 px-4 py-3 text-xs text-zinc-600" data-testid="booking-summary-note">
              Tip: If you’re unsure on strings, choose “Yes” for advice — we’ll follow up.
            </div>
          </Card>

          <Card testId="booking-pay-card" className="p-6">
            <div className="flex items-center justify-between gap-3" data-testid="booking-payment-header">
              <div className="text-sm font-semibold text-zinc-900" data-testid="booking-payment-title">
                PayPal payment
              </div>
              <div className="text-xs text-zinc-500" data-testid="booking-payment-price">
                ${config?.price_usd ?? 25}
              </div>
            </div>

            <p className="mt-2 text-sm text-zinc-600" data-testid="booking-payment-help">
              Your slot is confirmed only after payment.
              {config?.slot_hold_minutes ? ` We hold the slot for ${config.slot_hold_minutes} minutes while you check out.` : null}
            </p>

            <div className="mt-4" data-testid="paypal-area">
              {loadingConfig ? (
                <div className="text-sm text-zinc-500" data-testid="paypal-loading-config">
                  Loading…
                </div>
              ) : !config?.paypal_client_id ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900" data-testid="paypal-missing-config">
                  PayPal is not configured yet.
                </div>
              ) : (
                <>
                  <div className="rounded-2xl border border-zinc-200 bg-white/70 px-4 py-3" data-testid="paypal-button-shell">
                    <div className="mb-2 text-xs text-zinc-500" data-testid="paypal-note">
                      {isFormValid ? "Ready to pay" : "Complete all fields (including the questions) to enable payment"}
                    </div>

                    <div className="relative" data-testid="paypal-button-wrapper">
                      <div id="paypal-button-container" data-testid="paypal-button-container" />

                      {!isFormValid ? (
                        <div
                          className="absolute inset-0 grid place-items-center rounded-xl bg-white/60 backdrop-blur-sm"
                          data-testid="paypal-disabled-overlay"
                          title="Complete the form to continue"
                        >
                          <div className="text-xs font-medium text-zinc-700" data-testid="paypal-disabled-text">
                            Fill out the form to unlock PayPal
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {creatingOrder ? (
                    <div className="mt-2 text-xs text-zinc-500" data-testid="paypal-creating-order">
                      Preparing order…
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </Shell>
  );
}

function ConfirmationPage() {
  const { bookingId } = useParams();
  const query = useQuery();
  const navigate = useNavigate();
  const fromAdmin = query.get("from") === "admin";
  const token = query.get("t") || "";

  const [toast, setToast] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const [createAccount, setCreateAccount] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [creatingCustomerAccount, setCreatingCustomerAccount] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        if (!token) {
          setBooking(null);
          return;
        }
        const res = await axios.post(`${API}/bookings/${encodeURIComponent(bookingId)}/public`, { token });
        if (!mounted) return;
        setBooking(res.data);
      } catch {
        setToast({ type: "error", message: "Could not load booking details. Check your link." });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [bookingId, token]);

  const cancelBooking = async () => {
    try {
      await axios.post(`${API}/bookings/${encodeURIComponent(bookingId)}/cancel`, { token });
      setToast({ type: "success", message: "Booking cancelled." });
      const res = await axios.post(`${API}/bookings/${encodeURIComponent(bookingId)}/public`, { token });
      setBooking(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Cancellation failed.";
      setToast({ type: "error", message: msg });
    }
  };

  const handleCreateAccount = async () => {
    if (!token || newPassword.length < 8) return;
    if (!booking?.customer?.email) {
      setToast({ type: "error", message: "Missing booking email. Please refresh." });
      return;
    }

    setCreatingCustomerAccount(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        booking_id: bookingId,
        token,
        email: booking.customer.email,
        password: newPassword,
      });

      setCustomerToken(res.data.token);
      setToast({ type: "success", message: "Account created. Redirecting..." });
      // Redirect to account page
      setTimeout(() => {
        navigate("/account");
      }, 1000);

      setCreateAccount(false);
      setNewPassword("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to create account.";
      setToast({ type: "error", message: msg });
    } finally {
      setCreatingCustomerAccount(false);
    }
  };


  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="max-w-3xl" data-testid="confirmation-page">
        <div className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-900" data-testid="confirmation-pill">
          Booking confirmed
        </div>
        <h1 className="mt-4 text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="confirmation-title">
          You’re all set.
        </h1>
        <p className="mt-3 text-base text-zinc-600 md:text-lg" data-testid="confirmation-subtitle">
          We received your payment and confirmed your appointment.
        </p>

        <Card testId="confirmation-card" className="mt-7 p-6">
          <div className="text-sm font-semibold text-zinc-900" data-testid="confirmation-card-title">
            Appointment details
          </div>
          <div className="mt-3 space-y-2 text-sm text-zinc-700" data-testid="confirmation-card-body">
            <div className="flex items-center justify-between" data-testid="confirmation-row-booking-id">
              <span data-testid="confirmation-label-booking-id">Booking ID</span>
              <span className="font-mono text-xs text-zinc-900" data-testid="confirmation-value-booking-id">
                {bookingId}
              </span>
            </div>

            {loading ? (
              <div className="text-xs text-zinc-500" data-testid="confirmation-loading">
                Loading details…
              </div>
            ) : booking ? (
              <div className="mt-3 space-y-2" data-testid="confirmation-details">
                <div className="flex items-center justify-between" data-testid="confirmation-row-time">
                  <span data-testid="confirmation-label-time">Time</span>
                  <span className="font-medium text-zinc-900" data-testid="confirmation-value-time">
                    {booking.slot_start_local_label}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-testid="confirmation-row-name">
                  <span data-testid="confirmation-label-name">Name</span>
                  <span className="font-medium text-zinc-900" data-testid="confirmation-value-name">
                    {booking.customer.first_name} {booking.customer.last_name}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-testid="confirmation-row-phone">
                  <span data-testid="confirmation-label-phone">Phone</span>
                  <span className="font-medium text-zinc-900" data-testid="confirmation-value-phone">
                    {booking.customer.phone}
                  </span>
                </div>
                <div className="mt-3 rounded-2xl border border-zinc-200 bg-white/60 px-4 py-3" data-testid="confirmation-address-card">
                  <div className="text-xs font-semibold text-zinc-900" data-testid="confirmation-address-title">
                    Pickup & delivery address
                  </div>
                  <div className="mt-1 text-sm text-zinc-700" data-testid="confirmation-address-value">
                    {booking.pickup.address}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-zinc-500" data-testid="confirmation-note">
                Open this page from the confirmation link right after payment to see full details.
              </div>
            )}
          </div>
        </Card>

        {/* Always show account creation (your preference) */}
        <Card testId="create-account-card" className="mt-7 p-6">
          <div className="flex items-center justify-between gap-3" data-testid="create-account-header">
            <div className="text-sm font-semibold text-zinc-900" data-testid="create-account-title">
              Create an account
            </div>
            <span className="text-xs text-zinc-500" data-testid="create-account-domain">
              istring.club
            </span>
          </div>

          <div className="mt-3" data-testid="create-account-checkbox-row">
            <Checkbox
              testId="create-account-checkbox"
              checked={createAccount}
              onChange={(e) => setCreateAccount(e.target.checked)}
              label="Yes, create an account so I can manage my bookings and profile."
            />
          </div>

          {createAccount ? (
            <div className="mt-4 space-y-3" data-testid="create-account-form">
              <Input
                label="Create a password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                testId="create-account-password"
                required
                placeholder="Minimum 8 characters"
              />

              <div className="flex flex-wrap items-center gap-2" data-testid="create-account-actions">
                <PillButton
                  testId="create-account-submit-button"
                  variant="primary"
                  disabled={!token || newPassword.length < 8 || creatingCustomerAccount}
                  onClick={handleCreateAccount}
                  className="px-6 py-3"
                >
                  {creatingCustomerAccount ? "Creating…" : "Create account"}
                </PillButton>

                <Link to="/account" data-testid="create-account-go-account-link">
                  <PillButton testId="create-account-go-account-button" variant="ghost">
                    Go to Account
                  </PillButton>
                </Link>
              </div>

              {!token ? (
                <div className="text-xs text-zinc-500" data-testid="create-account-missing-token">
                  This option becomes available from the confirmation link right after payment.
                </div>
              ) : null}
            </div>
          ) : null}
        </Card>

        {!fromAdmin && token && booking?.status === "confirmed" ? (
          <Card testId="manage-booking-card" className="mt-6 p-6">
            <div className="text-sm font-semibold text-zinc-900" data-testid="manage-booking-title">
              Manage your booking
            </div>
            <p className="mt-2 text-sm text-zinc-600" data-testid="manage-booking-subtitle">
              Need to change plans? You can cancel or choose a new time.
            </p>

            <div className="mt-4 flex flex-wrap gap-2" data-testid="manage-booking-actions">
              <PillButton testId="manage-booking-cancel-button" variant="secondary" onClick={cancelBooking}>
                Cancel booking
              </PillButton>
              <Link to={`/reschedule/${encodeURIComponent(bookingId)}?t=${encodeURIComponent(token)}`} data-testid="manage-booking-reschedule-link">
                <PillButton testId="manage-booking-reschedule-button" variant="ghost">
                  Reschedule
                </PillButton>
              </Link>
            </div>
          </Card>
        ) : null}

        <div className="mt-7 flex flex-wrap gap-3" data-testid="confirmation-actions">
          <Link to="/book" data-testid="confirmation-book-another-link">
            <PillButton testId="confirmation-book-another-button" variant="secondary">
              Book another
            </PillButton>
          </Link>
          <Link to="/" data-testid="confirmation-home-link">
            <PillButton testId="confirmation-home-button" variant="ghost">
              Back to home
            </PillButton>
          </Link>
          {fromAdmin ? (
            <Link to="/admin" data-testid="confirmation-admin-link">
              <PillButton testId="confirmation-admin-button" variant="primary">
                Back to admin
              </PillButton>
            </Link>
          ) : null}
        </div>
      </div>
    </Shell>
  );
}

function ReschedulePage() {
  const { bookingId } = useParams();
  const query = useQuery();
  const token = query.get("t") || "";
  const navigate = useNavigate();

  const [toast, setToast] = useState(null);
  const [date, setDate] = useState(todayYMD());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    let mounted = true;
    const loadSlots = async () => {
      try {
        setLoadingSlots(true);
        const res = await axios.get(`${API}/slots`, { params: { date } });
        if (!mounted) return;
        setSlots(res.data);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Could not load slots.";
        setToast({ type: "error", message: msg });
      } finally {
        if (mounted) setLoadingSlots(false);
      }
    };
    loadSlots();
    return () => {
      mounted = false;
    };
  }, [date]);

  const reschedule = async () => {
    if (!selectedSlot) {
      setToast({ type: "error", message: "Select a new slot first." });
      return;
    }
    try {
      await axios.post(`${API}/bookings/${encodeURIComponent(bookingId)}/reschedule`, {
        token,
        slot_start_utc: selectedSlot.slot_start_utc,
      });
      setToast({ type: "success", message: "Rescheduled successfully." });
      navigate(`/confirmation/${encodeURIComponent(bookingId)}?t=${encodeURIComponent(token)}`, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Reschedule failed.";
      setToast({ type: "error", message: msg });
    }
  };

  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="max-w-4xl" data-testid="reschedule-page">
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="reschedule-title">
          Reschedule
        </h1>
        <p className="mt-3 text-base text-zinc-600 md:text-lg" data-testid="reschedule-subtitle">
          Pick a new 1-hour slot. (Your payment stays on file.)
        </p>

        <div className="mt-6 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]" data-testid="reschedule-grid">
          <Card testId="reschedule-date-card" className="p-6">
            <Select label="Date" value={date} onChange={(e) => setDate(e.target.value)} testId="reschedule-date-select">
              {Array.from({ length: 15 }, (_, i) => addDaysYMD(todayYMD(), i)).map((d) => (
                <option data-testid={`reschedule-date-option-${d}`} key={d} value={d}>
                  {formatDateLabel(d)}
                </option>
              ))}
            </Select>

            <div className="mt-4" data-testid="reschedule-slots">
              <div className="mb-2 text-sm font-semibold text-zinc-900" data-testid="reschedule-slots-title">
                Available times
              </div>
              {loadingSlots ? (
                <div className="text-sm text-zinc-500" data-testid="reschedule-slots-loading">
                  Loading…
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2" data-testid="reschedule-slots-grid">
                  {slots.map((s, idx) => {
                    const active = selectedSlot?.slot_start_utc === s.slot_start_utc;
                    return (
                      <button
                        key={s.slot_start_utc}
                        data-testid={`reschedule-slot-button-${idx}`}
                        disabled={!s.available}
                        onClick={() => setSelectedSlot(s)}
                        className={cx(
                          "rounded-2xl border px-3 py-3 text-left",
                          "transition-colors duration-200",
                          s.available ? "bg-white/70 hover:bg-white" : "bg-zinc-100 text-zinc-400 cursor-not-allowed",
                          active ? "border-zinc-900 ring-2 ring-zinc-200" : "border-zinc-200",
                        )}
                      >
                        <div className="text-sm font-semibold" data-testid={`reschedule-slot-label-${idx}`}>
                          {s.slot_start_local_label}
                        </div>
                        <div className="mt-0.5 text-xs text-zinc-500" data-testid={`reschedule-slot-availability-${idx}`}>
                          {s.available ? "Available" : "Unavailable"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          <Card testId="reschedule-action-card" className="p-6">
            <div className="text-sm font-semibold text-zinc-900" data-testid="reschedule-action-title">
              Confirm change
            </div>
            <p className="mt-2 text-sm text-zinc-600" data-testid="reschedule-action-help">
              Selected:{" "}
              <span className="font-medium text-zinc-900" data-testid="reschedule-selected-slot">
                {selectedSlot ? selectedSlot.slot_start_local_label : "—"}
              </span>
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="reschedule-action-buttons">
              <PillButton testId="reschedule-confirm-button" variant="primary" onClick={reschedule} disabled={!selectedSlot || !token}>
                Confirm reschedule
              </PillButton>
              <Link to={`/confirmation/${encodeURIComponent(bookingId)}?t=${encodeURIComponent(token)}`} data-testid="reschedule-back-link">
                <PillButton testId="reschedule-back-button" variant="ghost">
                  Back
                </PillButton>
              </Link>
            </div>
            {!token ? (
              <div className="mt-3 text-xs text-zinc-500" data-testid="reschedule-missing-token">
                This page must be opened from your confirmation link.
              </div>
            ) : null}
          </Card>
        </div>
      </div>
    </Shell>
  );
}

function CustomerLoginPage() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      setCustomerToken(res.data.token);
      navigate("/account", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      setToast({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="max-w-xl" data-testid="customer-login-page">
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="customer-login-title">
          Account
        </h1>
        <p className="mt-3 text-base text-zinc-600 md:text-lg" data-testid="customer-login-subtitle">
          Sign in to manage your profile and bookings.
        </p>

        <Card testId="customer-login-card" className="mt-7 p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="customer-login-form">
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} testId="customer-login-email" required />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              testId="customer-login-password"
              required
            />
            <div className="flex items-center gap-3" data-testid="customer-login-actions">
              <PillButton testId="customer-login-submit-button" type="submit" variant="primary" disabled={loading} className="px-6 py-3">
                {loading ? "Signing in…" : "Sign in"}
              </PillButton>
              <Link to="/" data-testid="customer-login-home-link" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors duration-200">
                Home
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </Shell>
  );
}

function CustomerAccountPage() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  const token = getCustomerToken();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");

  const load = React.useCallback(async () => {
    if (!token) {
      navigate("/account/login", { replace: true });
      return;
    }

    setLoading(true);
    try {
      const [meRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/my/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setProfile(meRes.data);
      setBookings(bookingsRes.data);

      setFirstName(meRes.data.first_name);
      setLastName(meRes.data.last_name);
      setPhone(meRes.data.phone);
      setAddress(meRes.data.default_address);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not load account.";
      setToast({ type: "error", message: msg });
      if (err?.response?.status === 401) {
        clearCustomerToken();
        navigate("/account/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const saveProfile = async () => {
    try {
      const res = await axios.patch(
        `${API}/me`,
        { first_name: firstName, last_name: lastName, phone, default_address: address },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProfile(res.data);
      setToast({ type: "success", message: "Profile updated." });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed.";
      setToast({ type: "error", message: msg });
    }
  };

  const logout = () => {
    clearCustomerToken();
    navigate("/account/login", { replace: true });
  };

  const cancelFromAccount = async (id) => {
    try {
      await axios.post(`${API}/my/bookings/${encodeURIComponent(id)}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setToast({ type: "success", message: "Booking cancelled." });
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Cancel failed.";
      setToast({ type: "error", message: msg });
    }
  };

  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div data-testid="customer-account-page">
        <div className="flex flex-wrap items-end justify-between gap-4" data-testid="customer-account-header">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="customer-account-title">
              Your account
            </h1>
            <p className="mt-2 text-base text-zinc-600 md:text-lg" data-testid="customer-account-subtitle">
              Profile and booking history.
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="customer-account-actions">
            <PillButton testId="customer-account-refresh-button" variant="secondary" onClick={load}>
              Refresh
            </PillButton>
            <PillButton testId="customer-account-logout-button" variant="ghost" onClick={logout}>
              Logout
            </PillButton>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-zinc-500" data-testid="customer-account-loading">
            Loading…
          </div>
        ) : (
          <div className="mt-6 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]" data-testid="customer-account-grid">
            <Card testId="customer-profile-card" className="p-6">
              <div className="text-sm font-semibold text-zinc-900" data-testid="customer-profile-title">
                Profile
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="customer-profile-grid">
                <Input label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} testId="customer-profile-first-name" required />
                <Input label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} testId="customer-profile-last-name" required />
                <Input label="Email" value={profile?.email || ""} onChange={() => { }} testId="customer-profile-email" type="email" required disabled />
                <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} testId="customer-profile-phone" required />
              </div>
              <div className="mt-4" data-testid="customer-profile-address-wrapper">
                <Input label="Default address" value={address} onChange={(e) => setAddress(e.target.value)} testId="customer-profile-address" required />
              </div>
              <div className="mt-4" data-testid="customer-profile-save-row">
                <PillButton testId="customer-profile-save-button" variant="primary" onClick={saveProfile}>
                  Save changes
                </PillButton>
              </div>
            </Card>

            <Card testId="customer-bookings-card" className="p-6">
              <div className="flex items-center justify-between gap-3" data-testid="customer-bookings-header">
                <div className="text-sm font-semibold text-zinc-900" data-testid="customer-bookings-title">
                  Booking history
                </div>
                <div className="text-xs text-zinc-500" data-testid="customer-bookings-count">
                  {bookings.length} total
                </div>
              </div>

              {bookings.length === 0 ? (
                <div className="mt-4 text-sm text-zinc-500" data-testid="customer-bookings-empty">
                  No bookings yet.
                </div>
              ) : (
                <div className="mt-4 space-y-3" data-testid="customer-bookings-list">
                  {bookings.map((b, idx) => (
                    <details
                      key={b.booking_id}
                      className="group rounded-3xl border border-zinc-200 bg-white/70 shadow-sm"
                      data-testid={`customer-booking-item-${idx}`}
                    >
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4" data-testid={`customer-booking-summary-${idx}`}>
                        <div>
                          <div className="text-sm font-semibold text-zinc-900" data-testid={`customer-booking-time-${idx}`}>
                            {b.slot_start_local_label}
                          </div>
                          <div className="mt-1 text-xs text-zinc-500" data-testid={`customer-booking-meta-${idx}`}>
                            Status: {b.status} • ${b.price_usd}
                          </div>
                        </div>
                        <div className="text-xs text-zinc-500 transition-colors duration-200 group-open:text-zinc-900" data-testid={`customer-booking-expand-${idx}`}>
                          Details
                        </div>
                      </summary>
                      <div className="px-5 pb-5" data-testid={`customer-booking-details-${idx}`}>
                        <div className="text-sm text-zinc-700" data-testid={`customer-booking-address-${idx}`}>
                          {b.pickup.address}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`customer-booking-actions-${idx}`}>
                          <Link
                            to={`/account/booking/${encodeURIComponent(b.booking_id)}`}
                            data-testid={`customer-booking-view-link-${idx}`}
                          >
                            <PillButton testId={`customer-booking-view-button-${idx}`} variant="ghost">
                              View
                            </PillButton>
                          </Link>
                          <Link
                            to={`/account/reschedule/${encodeURIComponent(b.booking_id)}`}
                            data-testid={`customer-booking-reschedule-link-${idx}`}
                          >
                            <PillButton testId={`customer-booking-reschedule-button-${idx}`} variant="ghost" disabled={b.status !== "confirmed"}>
                              Reschedule
                            </PillButton>
                          </Link>
                          <PillButton
                            testId={`customer-booking-cancel-button-${idx}`}
                            variant="secondary"
                            onClick={() => cancelFromAccount(b.booking_id)}
                            disabled={b.status !== "confirmed"}
                          >
                            Cancel
                          </PillButton>
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </Shell>
  );
}

function AccountBookingPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const token = getCustomerToken();

  const [toast, setToast] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        if (!token) {
          navigate("/account/login", { replace: true });
          return;
        }
        const res = await axios.get(`${API}/my/bookings`, { headers: { Authorization: `Bearer ${token}` } });
        const found = res.data.find((b) => b.booking_id === bookingId);
        if (!mounted) return;
        setBooking(found || null);
      } catch {
        setToast({ type: "error", message: "Could not load booking." });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [bookingId, token, navigate]);

  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="max-w-3xl" data-testid="account-booking-page">
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="account-booking-title">
          Booking
        </h1>
        <p className="mt-3 text-base text-zinc-600 md:text-lg" data-testid="account-booking-subtitle">
          Details from your account.
        </p>

        <Card testId="account-booking-card" className="mt-7 p-6">
          {loading ? (
            <div className="text-sm text-zinc-500" data-testid="account-booking-loading">
              Loading…
            </div>
          ) : !booking ? (
            <div className="text-sm text-zinc-500" data-testid="account-booking-missing">
              Booking not found.
            </div>
          ) : (
            <div className="space-y-2 text-sm text-zinc-700" data-testid="account-booking-details">
              <div className="flex items-center justify-between" data-testid="account-booking-row-id">
                <span data-testid="account-booking-label-id">Booking ID</span>
                <span className="font-mono text-xs text-zinc-900" data-testid="account-booking-value-id">
                  {booking.booking_id}
                </span>
              </div>
              <div className="flex items-center justify-between" data-testid="account-booking-row-time">
                <span data-testid="account-booking-label-time">Time</span>
                <span className="font-medium text-zinc-900" data-testid="account-booking-value-time">
                  {booking.slot_start_local_label}
                </span>
              </div>
              <div className="flex items-center justify-between" data-testid="account-booking-row-status">
                <span data-testid="account-booking-label-status">Status</span>
                <span className="font-medium text-zinc-900" data-testid="account-booking-value-status">
                  {booking.status}
                </span>
              </div>
              <div className="mt-3 rounded-2xl border border-zinc-200 bg-white/60 px-4 py-3" data-testid="account-booking-address-card">
                <div className="text-xs font-semibold text-zinc-900" data-testid="account-booking-address-title">
                  Pickup & delivery address
                </div>
                <div className="mt-1 text-sm text-zinc-700" data-testid="account-booking-address-value">
                  {booking.pickup.address}
                </div>
              </div>
            </div>
          )}
        </Card>

        <div className="mt-6" data-testid="account-booking-back-row">
          <Link to="/account" data-testid="account-booking-back-link">
            <PillButton testId="account-booking-back-button" variant="secondary">
              Back to account
            </PillButton>
          </Link>
        </div>
      </div>
    </Shell>
  );
}

function AccountReschedulePage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const token = getCustomerToken();

  const [toast, setToast] = useState(null);
  const [date, setDate] = useState(todayYMD());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate("/account/login", { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    let mounted = true;
    const loadSlots = async () => {
      try {
        setLoadingSlots(true);
        const res = await axios.get(`${API}/slots`, { params: { date } });
        if (!mounted) return;
        setSlots(res.data);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Could not load slots.";
        setToast({ type: "error", message: msg });
      } finally {
        if (mounted) setLoadingSlots(false);
      }
    };
    loadSlots();
    return () => {
      mounted = false;
    };
  }, [date]);

  const reschedule = async () => {
    if (!selectedSlot) {
      setToast({ type: "error", message: "Select a new slot first." });
      return;
    }
    try {
      await axios.post(
        `${API}/my/bookings/${encodeURIComponent(bookingId)}/reschedule`,
        { slot_start_utc: selectedSlot.slot_start_utc },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setToast({ type: "success", message: "Rescheduled successfully." });
      navigate("/account", { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Reschedule failed.";
      setToast({ type: "error", message: msg });
    }
  };

  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="max-w-4xl" data-testid="account-reschedule-page">
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="account-reschedule-title">
          Reschedule
        </h1>
        <p className="mt-3 text-base text-zinc-600 md:text-lg" data-testid="account-reschedule-subtitle">
          Choose a new slot from your account.
        </p>

        <div className="mt-6 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]" data-testid="account-reschedule-grid">
          <Card testId="account-reschedule-date-card" className="p-6">
            <Select label="Date" value={date} onChange={(e) => setDate(e.target.value)} testId="account-reschedule-date-select">
              {Array.from({ length: 15 }, (_, i) => addDaysYMD(todayYMD(), i)).map((d) => (
                <option data-testid={`account-reschedule-date-option-${d}`} key={d} value={d}>
                  {formatDateLabel(d)}
                </option>
              ))}
            </Select>

            <div className="mt-4" data-testid="account-reschedule-slots">
              <div className="mb-2 text-sm font-semibold text-zinc-900" data-testid="account-reschedule-slots-title">
                Available times
              </div>
              {loadingSlots ? (
                <div className="text-sm text-zinc-500" data-testid="account-reschedule-slots-loading">
                  Loading…
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2" data-testid="account-reschedule-slots-grid">
                  {slots.map((s, idx) => {
                    const active = selectedSlot?.slot_start_utc === s.slot_start_utc;
                    return (
                      <button
                        key={s.slot_start_utc}
                        data-testid={`account-reschedule-slot-button-${idx}`}
                        disabled={!s.available}
                        onClick={() => setSelectedSlot(s)}
                        className={cx(
                          "rounded-2xl border px-3 py-3 text-left",
                          "transition-colors duration-200",
                          s.available ? "bg-white/70 hover:bg-white" : "bg-zinc-100 text-zinc-400 cursor-not-allowed",
                          active ? "border-zinc-900 ring-2 ring-zinc-200" : "border-zinc-200",
                        )}
                      >
                        <div className="text-sm font-semibold" data-testid={`account-reschedule-slot-label-${idx}`}>
                          {s.slot_start_local_label}
                        </div>
                        <div className="mt-0.5 text-xs text-zinc-500" data-testid={`account-reschedule-slot-availability-${idx}`}>
                          {s.available ? "Available" : "Unavailable"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          <Card testId="account-reschedule-action-card" className="p-6">
            <div className="text-sm font-semibold text-zinc-900" data-testid="account-reschedule-action-title">
              Confirm change
            </div>
            <p className="mt-2 text-sm text-zinc-600" data-testid="account-reschedule-action-help">
              Selected:{" "}
              <span className="font-medium text-zinc-900" data-testid="account-reschedule-selected-slot">
                {selectedSlot ? selectedSlot.slot_start_local_label : "—"}
              </span>
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="account-reschedule-action-buttons">
              <PillButton
                testId="account-reschedule-confirm-button"
                variant="primary"
                onClick={reschedule}
                disabled={!selectedSlot || !token}
              >
                Confirm reschedule
              </PillButton>
              <Link to="/account" data-testid="account-reschedule-back-link">
                <PillButton testId="account-reschedule-back-button" variant="ghost">
                  Back
                </PillButton>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </Shell>
  );
}

function AdminLoginPage() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("istring_admin_token", res.data.token);
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      setToast({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="max-w-xl" data-testid="admin-login-page">
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="admin-login-title">
          Admin
        </h1>
        <p className="mt-3 text-base text-zinc-600 md:text-lg" data-testid="admin-login-subtitle">
          Sign in to manage bookings.
        </p>

        <Card testId="admin-login-card" className="mt-7 p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} testId="admin-login-email" required />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              testId="admin-login-password"
              required
            />
            <div className="flex items-center gap-3" data-testid="admin-login-actions">
              <PillButton testId="admin-login-submit-button" type="submit" variant="primary" disabled={loading} className="px-6 py-3">
                {loading ? "Signing in…" : "Sign in"}
              </PillButton>
              <Link
                to="/"
                data-testid="admin-login-back-home-link"
                className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors duration-200"
              >
                Back to home
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </Shell>
  );
}

function AdminDashboardPage() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  const token = localStorage.getItem("istring_admin_token");

  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);

  const load = React.useCallback(async () => {
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings`, {
        params: { status: statusFilter || undefined, date: dateFilter || undefined },
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not load bookings.";
      setToast({ type: "error", message: msg });
      if (err?.response?.status === 401) {
        localStorage.removeItem("istring_admin_token");
        navigate("/admin/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, statusFilter, dateFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    localStorage.removeItem("istring_admin_token");
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${encodeURIComponent(bookingId)}`,
        { status },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setToast({ type: "success", message: `Updated to ${status}.` });
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed.";
      setToast({ type: "error", message: msg });
    }
  };

  const statusBadge = (s) => {
    const styles =
      s === "confirmed"
        ? "bg-emerald-50 text-emerald-900 border-emerald-200"
        : s === "pending_payment"
          ? "bg-amber-50 text-amber-900 border-amber-200"
          : s === "completed"
            ? "bg-zinc-900 text-white border-zinc-900"
            : "bg-zinc-100 text-zinc-700 border-zinc-200";
    return (
      <span className={cx("rounded-full border px-2 py-1 text-xs", styles)} data-testid={`admin-status-badge-${s}`}>
        {s}
      </span>
    );
  };

  return (
    <Shell>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div data-testid="admin-dashboard-page">
        <div className="flex flex-wrap items-end justify-between gap-4" data-testid="admin-dashboard-header">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl" data-testid="admin-dashboard-title">
              Bookings
            </h1>
            <p className="mt-2 text-base text-zinc-600 md:text-lg" data-testid="admin-dashboard-subtitle">
              Manage appointment requests and statuses.
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="admin-dashboard-actions">
            <PillButton testId="admin-refresh-button" variant="secondary" onClick={load}>
              Refresh
            </PillButton>
            <PillButton testId="admin-logout-button" variant="ghost" onClick={logout}>
              Logout
            </PillButton>
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="admin-filters">
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} testId="admin-filter-status">
            <option data-testid="admin-filter-status-all" value="">
              All
            </option>
            {["pending_payment", "confirmed", "completed", "cancelled", "expired"].map((s) => (
              <option data-testid={`admin-filter-status-${s}`} key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>

          <Input label="Date" type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} testId="admin-filter-date" />

          <div className="rounded-3xl border border-zinc-200 bg-white/60 p-4" data-testid="admin-filter-hint">
            <div className="text-xs text-zinc-500" data-testid="admin-filter-hint-text">
              Tip: filter by date to see a single day’s route.
            </div>
          </div>
        </div>

        <div className="mt-6" data-testid="admin-bookings-section">
          {loading ? (
            <div className="text-sm text-zinc-500" data-testid="admin-bookings-loading">
              Loading…
            </div>
          ) : bookings.length === 0 ? (
            <Card testId="admin-bookings-empty" className="p-6">
              <div className="text-sm text-zinc-700" data-testid="admin-bookings-empty-text">
                No bookings found.
              </div>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="admin-bookings-list">
              {bookings.map((b, idx) => (
                <details
                  key={b.booking_id}
                  className="group rounded-3xl border border-zinc-200 bg-white/70 shadow-sm"
                  data-testid={`admin-booking-item-${idx}`}
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4" data-testid={`admin-booking-summary-${idx}`}>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2" data-testid={`admin-booking-summary-top-${idx}`}>
                        <div className="text-sm font-semibold text-zinc-900" data-testid={`admin-booking-customer-${idx}`}>
                          {b.customer.first_name} {b.customer.last_name}
                        </div>
                        {statusBadge(b.status)}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500" data-testid={`admin-booking-summary-bottom-${idx}`}>
                        {b.slot_start_local_label} • {b.customer.phone} • ${b.price_usd}
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500 transition-colors duration-200 group-open:text-zinc-900" data-testid={`admin-booking-expand-${idx}`}>
                      Details
                    </div>
                  </summary>

                  <div className="px-5 pb-5" data-testid={`admin-booking-details-${idx}`}>
                    <div className="grid gap-3 md:grid-cols-2" data-testid={`admin-booking-details-grid-${idx}`}>
                      <Card testId={`admin-booking-card-contact-${idx}`} className="p-4">
                        <div className="text-xs font-semibold text-zinc-900" data-testid={`admin-contact-title-${idx}`}>
                          Contact
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-zinc-700" data-testid={`admin-contact-body-${idx}`}>
                          <div data-testid={`admin-contact-email-${idx}`}>{b.customer.email}</div>
                          <div data-testid={`admin-contact-phone-${idx}`}>{b.customer.phone}</div>
                        </div>
                      </Card>

                      <Card testId={`admin-booking-card-pickup-${idx}`} className="p-4">
                        <div className="text-xs font-semibold text-zinc-900" data-testid={`admin-pickup-title-${idx}`}>
                          Pickup / Delivery
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-zinc-700" data-testid={`admin-pickup-body-${idx}`}>
                          <div data-testid={`admin-pickup-address-${idx}`}>{b.pickup.address}</div>
                          <div className="text-xs text-zinc-500" data-testid={`admin-pickup-within15-${idx}`}>
                            Within 20 miles: {b.pickup.within_15_miles ? "Yes" : "No"}
                          </div>
                        </div>
                      </Card>

                      <Card testId={`admin-booking-card-strings-${idx}`} className="p-4">
                        <div className="text-xs font-semibold text-zinc-900" data-testid={`admin-strings-title-${idx}`}>
                          Strings
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-zinc-700" data-testid={`admin-strings-body-${idx}`}>
                          <div data-testid={`admin-strings-own-${idx}`}>Own strings: {b.questionnaire.has_own_strings ? "Yes" : "No"}</div>
                          <div data-testid={`admin-strings-advice-${idx}`}>Needs advice: {b.questionnaire.needs_advice ? "Yes" : "No"}</div>
                          <div data-testid={`admin-strings-tensions-${idx}`}>
                            Tension: mains {b.questionnaire.tension_mains} / crosses {b.questionnaire.tension_crosses}
                          </div>
                        </div>
                      </Card>

                      <Card testId={`admin-booking-card-paypal-${idx}`} className="p-4">
                        <div className="text-xs font-semibold text-zinc-900" data-testid={`admin-paypal-title-${idx}`}>
                          PayPal
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-zinc-700" data-testid={`admin-paypal-body-${idx}`}>
                          <div data-testid={`admin-paypal-order-${idx}`}>
                            Order: <span className="font-mono text-xs">{b.paypal?.order_id || "—"}</span>
                          </div>
                          <div data-testid={`admin-paypal-capture-${idx}`}>
                            Capture: <span className="font-mono text-xs">{b.paypal?.capture_id || "—"}</span>
                          </div>
                        </div>
                      </Card>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2" data-testid={`admin-booking-actions-${idx}`}>
                      <PillButton
                        testId={`admin-mark-completed-button-${idx}`}
                        variant="primary"
                        onClick={() => updateStatus(b.booking_id, "completed")}
                        disabled={b.status !== "confirmed"}
                      >
                        Mark completed
                      </PillButton>
                      <PillButton
                        testId={`admin-cancel-booking-button-${idx}`}
                        variant="secondary"
                        onClick={() => updateStatus(b.booking_id, "cancelled")}
                        disabled={b.status === "cancelled" || b.status === "completed"}
                      >
                        Cancel
                      </PillButton>

                    </div>
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function AdminGate() {
  const navigate = useNavigate();
  const token = localStorage.getItem("istring_admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login", { replace: true });
    }
  }, [token, navigate]);

  if (!token) {
    return null;
  }

  return <AdminDashboardPage />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/book" element={<BookingPage />} />
        <Route path="/confirmation/:bookingId" element={<ConfirmationPage />} />
        <Route path="/reschedule/:bookingId" element={<ReschedulePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        <Route path="/account/login" element={<CustomerLoginPage />} />
        <Route path="/account" element={<CustomerAccountPage />} />
        <Route path="/account/booking/:bookingId" element={<AccountBookingPage />} />
        <Route path="/account/reschedule/:bookingId" element={<AccountReschedulePage />} />

        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminGate />} />
      </Routes>
    </BrowserRouter>
  );
}
